import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Car, Gamepad2, Trophy, LogOut, Coins, Shield, Crown, Lightbulb, Hammer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/dashboard", label: "Hub", icon: Home, testid: "nav-dashboard" },
  { to: "/garage", label: "Garage", icon: Car, testid: "nav-garage" },
  { to: "/lobbies", label: "Lobbies", icon: Gamepad2, testid: "nav-lobbies" },
  { to: "/courses", label: "Courses", icon: Hammer, testid: "nav-courses" },
  { to: "/bosses", label: "Bosses", icon: Crown, testid: "nav-bosses" },
  { to: "/ideas", label: "Ideas", icon: Lightbulb, testid: "nav-ideas" },
  { to: "/leaderboard", label: "Rankings", icon: Trophy, testid: "nav-leaderboard" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <aside
      className="hidden lg:flex flex-col w-[260px] shrink-0 border-r border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 h-screen"
      data-testid="app-sidebar"
    >
      <div className="px-6 pt-8 pb-6 border-b border-white/10">
        <div className="label-kicker">Retro Arcade Racer</div>
        <div
          className="font-heading text-3xl mt-1 cursor-pointer leading-[0.95] tracking-tight transition-colors"
          onClick={() => navigate("/dashboard")}
          data-testid="brand-logo"
        >
          <span className="text-[#FFCC00]">NITRO</span><br />
          <span className="text-white">AUTO</span>{" "}
          <span className="text-[#FF3B30]">HEIST</span>
        </div>
        <div className="mt-4 h-[3px] w-16 bg-[#FF3B30]" />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {links.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm tracking-wider uppercase transition-all duration-150 ${
                isActive
                  ? "bg-[#FF3B30]/15 text-white border-l-2 border-[#FF3B30]"
                  : "text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={2.2} />
            <span className="font-heading">{label}</span>
          </NavLink>
        ))}
        {user?.is_admin && (
          <NavLink
            to="/admin"
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm tracking-wider uppercase transition-all duration-150 ${
                isActive
                  ? "bg-[#FFCC00]/15 text-white border-l-2 border-[#FFCC00]"
                  : "text-[#FFCC00]/80 hover:text-[#FFCC00] hover:bg-white/5 border-l-2 border-transparent"
              }`
            }
          >
            <Shield className="w-4 h-4" strokeWidth={2.2} />
            <span className="font-heading">Admin</span>
          </NavLink>
        )}
      </nav>

      <div className="px-4 py-4 border-t border-white/10 space-y-3">
        {user && (
          <div className="space-y-2" data-testid="sidebar-user-card">
            <div
              onClick={() => navigate(user.is_admin ? "/admin" : "/garage")}
              data-testid="sidebar-user-pic-btn"
              role="button"
              title={user.is_admin ? "Open admin panel" : "Open garage"}
              className="flex items-center gap-3 -mx-2 px-2 py-2 cursor-pointer hover:bg-white/5 transition-colors group"
            >
              <div className="relative">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover border border-white/10 group-hover:border-[#FFCC00]/60 transition-colors" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#FF3B30]/80 flex items-center justify-center text-black font-bold">
                    {user.name?.[0]?.toUpperCase() ?? "R"}
                  </div>
                )}
                {user.is_admin && (
                  <span
                    className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#FFCC00] border-2 border-black flex items-center justify-center"
                    data-testid="sidebar-admin-badge"
                    title="Admin"
                  >
                    <Shield className="w-2 h-2 text-black" strokeWidth={3} />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</div>
                <div className="flex items-center gap-1 text-xs text-[#FFCC00]" data-testid="sidebar-user-currency">
                  <Coins className="w-3 h-3" />
                  <span className="font-mono">{user.currency?.toLocaleString?.() ?? 0}</span>
                </div>
              </div>
            </div>
            {user.is_admin && (
              <NavLink
                to="/admin"
                data-testid="sidebar-admin-link"
                className="flex items-center justify-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black border border-[#FFCC00]/40 py-2 transition-colors"
              >
                <Shield className="w-3 h-3" />
                Admin Panel
              </NavLink>
            )}
          </div>
        )}
        <button
          onClick={logout}
          data-testid="sidebar-logout"
          className="w-full flex items-center justify-center gap-2 text-xs tracking-[0.25em] uppercase text-white/60 hover:text-white border border-white/10 hover:border-white/30 py-3 transition-colors duration-150"
        >
          <LogOut className="w-3.5 h-3.5" />
          Logout
        </button>
      </div>
    </aside>
  );
}
